'use client'

import { useState, useEffect } from 'react'
import { SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline' 
import dynamic from 'next/dynamic'

const iconProps = { className: "w-6 h-6" }

const icons = {
  light: <SunIcon {...iconProps} />, 
  dark: <MoonIcon {...iconProps} />,
  system: <ComputerDesktopIcon {...iconProps} />
}

type ThemeKey = keyof typeof icons;
const themeCycle: ThemeKey[] = ['light', 'dark'];

// Fallback button for loading state - keep exactly the same between server and client
function FallbackButton() {
  return (
    <button
      className="relative flex items-center justify-center w-8 h-8 rounded-full text-text-main"
      aria-label="Theme switcher loading"
    >
      <span className="sr-only">Loading theme</span>
    </button>
  );
}

// Load ThemeSwitcherWithContext component dynamically with SSR disabled
// This ensures it only runs on the client, avoiding hydration mismatches
const DynamicThemeSwitcherWithContext = dynamic(
  () => import('./ThemeSwitcherWithContext'),
  { 
    ssr: false,
    loading: FallbackButton
  }
);

export default function ThemeSwitcher() {
  const [mounted, setMounted] = useState(false);
  const [theme, setTheme] = useState<ThemeKey>('system');
  const [activeTheme, setActiveTheme] = useState<'light' | 'dark'>('dark');
  const [canUseContext, setCanUseContext] = useState(false);
  
  // Determine if we can use ThemeContext (client-side only)
  useEffect(() => {
    setMounted(true);
    
    // Initialize theme from localStorage
    try {
      const storedTheme = localStorage.getItem('themePreference');
      if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
        setTheme(storedTheme as ThemeKey);
      }
      
      // If system preference, determine active theme
      if (storedTheme === 'system' || !storedTheme) {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setActiveTheme(prefersDark ? 'dark' : 'light');
      } else if (storedTheme === 'light' || storedTheme === 'dark') {
        setActiveTheme(storedTheme);
      }
    } catch {
      // Ignore localStorage errors
    }
    
    // Check if ThemeContext is available by just trying to import it
    try {
      // Just check if we can import ThemeProvider from the right path
      import('@/app/[locale]/ThemeProvider')
        .then(() => {
          setCanUseContext(true);
        })
        .catch(() => {
          setCanUseContext(false);
        });
    } catch {
      setCanUseContext(false);
    }
  }, []);
  
  // Essential for SSR: Return identical button before mounting
  // This ensures server and client render the same initial HTML
  if (!mounted) {
    return <FallbackButton />;
  }
  
  // Function to cycle through themes
  const cycleTheme = () => {
    // If currently using system preference, switch to light
    if (theme === 'system') {
      const nextTheme = 'light';
      setTheme(nextTheme);
      localStorage.setItem('themePreference', nextTheme);
      setActiveTheme(nextTheme);
      
      // Apply theme to document
      const root = document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(nextTheme);
      return;
    }
    
    const currentIndex = themeCycle.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themeCycle.length;
    const nextTheme = themeCycle[nextIndex];
    
    setTheme(nextTheme);
    localStorage.setItem('themePreference', nextTheme);
    
    // Update active theme
    setActiveTheme(nextTheme as 'light' | 'dark');
    
    // Apply theme to document
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(nextTheme);
  };
  
  // If we have access to ThemeContext, load the context-aware switcher
  // But only after mounting to prevent hydration mismatches
  if (canUseContext && mounted) {
    return <DynamicThemeSwitcherWithContext />;
  }
  
  // Fallback to local state when context not available
  return (
    <button
      onClick={cycleTheme}
      className="relative flex items-center justify-center w-8 h-8 rounded-full text-text-main transition-colors duration-200 focus:outline-none"
      aria-label={`Switch theme (current: ${theme})`}
      title={`Switch theme (current: ${theme})`}
    >
      <span className="absolute group-hover:scale-110 transition-transform duration-200">
        {icons[activeTheme]}
      </span>
    </button>
  );
} 